import React, { useState, useEffect, useRef } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { CalendarPlus, CalendarCheck, SpinnerGap, Paperclip, Info, WarningCircle, CheckCircle, XCircle, FileText, Image, Receipt, Signature, Download, Eraser } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SignaturePad from '@/components/SignaturePad';
import ApprovalProgress, { leaveProgressSteps, clearanceProgressSteps } from '@/components/ApprovalProgress';
import CashOnlyEncashmentSection from '@/components/CashOnlyEncashmentSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

// Saudi Labor Law leave rules
const LEAVE_RULES = {
  annual: {
    deducts_from_balance: true,
    icon_color: 'text-blue-600',
    bg_color: 'bg-blue-50 border-blue-200',
  },
  sick: {
    deducts_from_balance: false,
    icon_color: 'text-amber-600',
    bg_color: 'bg-amber-50 border-amber-200',
  },
  unpaid: {
    deducts_from_balance: false,
    icon_color: 'text-red-600',
    bg_color: 'bg-red-50 border-red-200',
  },
};

// Percentage doctors (طبيب نسبة) & agency contracted workers do NOT
// accrue paid leave — their leaves are informational only. Any UI that
// shows balance/allowance/clearance must be hidden for them. Percentage
// doctors are auto-flagged `is_contracted_worker=True` at create/update
// time, but we double-check `doctor_type==='percentage'` defensively so
// legacy records without the flag still render correctly.
const hasNoPaidLeave = (u) =>
  !!(u && (u.is_contracted_worker || (u.is_doctor && u.doctor_type === 'percentage')));

export default function EmployeeLeaves() {
  const { t } = useTranslation();
  const { direction, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState([]);
  const [myClearances, setMyClearances] = useState([]);
  const [activeClearance, setActiveClearance] = useState(null); // dialog state
  const [signatureName, setSignatureName] = useState('');
  const [signaturePadEmpty, setSignaturePadEmpty] = useState(true);
  const signaturePadRef = useRef(null);
  const [signing, setSigning] = useState(false);
  const [requestingClearanceLeaveId, setRequestingClearanceLeaveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [projection, setProjection] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    reason: '',
    attachment_url: null,
    destination_city: '',
  });

  useEffect(() => { fetchLeaves(); fetchBalance(); fetchMyClearances(); }, []);

  // Auto-refresh clearances (and re-open the active dialog against a fresh
  // fetch) whenever the tab regains focus / becomes visible. Covers the
  // scenario where the employee has the sign-off dialog open while the
  // admin/finance manager updates the amount in another session — without
  // this, the employee would sign against a stale amount.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      fetchMyClearances();
      if (activeClearance?.id) {
        openClearanceDetails(activeClearance.id);
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [activeClearance?.id]);

  const fetchLeaves = async () => {
    try {
      const { data } = await axios.get(`${API}/leaves?scope=own`, { withCredentials: true });
      setLeaves(data);
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyClearances = async () => {
    try {
      // Cache-bust with a timestamp + explicit no-cache headers so
      // upstream proxies / the browser cache can never serve a stale
      // clearance amount after the admin has updated it. Reported bug
      // 2026-07-15 — employee saw the OLD amount while admin saw the new.
      const { data } = await axios.get(`${API}/clearance/my`, {
        withCredentials: true,
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      setMyClearances(data || []);
    } catch (e) {
      // silent
    }
  };

  const requestClearance = async (leaveId) => {
    setRequestingClearanceLeaveId(leaveId);
    try {
      await axios.post(`${API}/clearance/request`, { leave_id: leaveId }, { withCredentials: true });
      toast.success(language === 'ar' ? 'تم إرسال طلب المخالصة. ستتمكن من رؤية التفاصيل بعد مراجعة الإدارة.' : 'Clearance request submitted. Details will be visible after admin review.');
      fetchMyClearances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setRequestingClearanceLeaveId(null);
    }
  };

  const openClearanceDetails = async (clearanceId) => {
    try {
      // Force-refresh: bust any HTTP cache so the employee never signs
      // against an outdated amount (see fetchMyClearances comment).
      const { data } = await axios.get(`${API}/clearance/${clearanceId}`, {
        withCredentials: true,
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      setActiveClearance(data);
      setSignatureName('');
      setSignaturePadEmpty(true);
      // Clear any previous drawing when reopening.
      setTimeout(() => signaturePadRef.current?.clear(), 50);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const submitSignature = async () => {
    if (!activeClearance || !signatureName.trim()) {
      toast.error(language === 'ar' ? 'يرجى كتابة اسمك للتوقيع' : 'Please type your name to sign');
      return;
    }
    const sigImage = signaturePadRef.current?.toDataURL();
    if (!sigImage) {
      toast.error(language === 'ar' ? 'يرجى رسم توقيعك في المربع المخصص' : 'Please draw your signature in the box');
      return;
    }
    setSigning(true);
    try {
      await axios.post(
        `${API}/clearance/${activeClearance.id}/sign`,
        { signature_name: signatureName.trim(), agreed: true, signature_image: sigImage },
        { withCredentials: true }
      );
      toast.success(language === 'ar' ? 'تم التوقيع بنجاح' : 'Signed successfully');
      setActiveClearance(null);
      fetchMyClearances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSigning(false);
    }
  };

  const downloadClearancePdf = (id) => {
    window.open(`${API}/clearance/${id}/pdf`, '_blank');
  };

  // Map leave_id → clearance for quick lookup in the table.
  // If multiple clearances exist for the same leave (e.g. after a
  // rejection + resubmission), prefer the most recent NON-rejected
  // clearance so the UI shows the current active request rather than
  // the old rejected one.
  const clearanceByLeaveId = myClearances.reduce((acc, c) => {
    const existing = acc[c.leave_id];
    if (!existing) {
      acc[c.leave_id] = c;
      return acc;
    }
    const isActive = (x) => x && x.status !== 'rejected';
    if (isActive(c) && !isActive(existing)) {
      acc[c.leave_id] = c;
    } else if (isActive(c) === isActive(existing)) {
      // Both active OR both rejected → keep the most recent by created_at.
      const ta = new Date(c.created_at || 0).getTime();
      const tb = new Date(existing.created_at || 0).getTime();
      if (ta > tb) acc[c.leave_id] = c;
    }
    return acc;
  }, {});

  const fetchBalance = async () => {
    try {
      const { data } = await axios.get(`${API}/leaves/my-balance`, { withCredentials: true });
      setLeaveBalance(data);
    } catch (error) {
      console.error('Failed to fetch leave balance:', error);
    }
  };

  // Live projection — re-fetches whenever the leave dates / type change
  // inside the open request dialog. Only meaningful for annual/emergency.
  useEffect(() => {
    if (!showRequestDialog) { setProjection(null); return; }
    if (!formData.start_date) { setProjection(null); return; }
    if (!['annual'].includes(formData.leave_type)) {
      setProjection(null);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/leaves/projected-balance`, {
          params: {
            start_date: formData.start_date,
            end_date: formData.end_date || formData.start_date,
            leave_type: formData.leave_type,
          },
          withCredentials: true,
          signal: controller.signal,
        });
        setProjection(data);
      } catch (e) {
        if (e.name !== 'CanceledError') console.error('projection failed', e);
      }
    }, 250);  // debounce 250ms — avoid hammering on every keystroke
    return () => { controller.abort(); clearTimeout(t); };
  }, [showRequestDialog, formData.start_date, formData.end_date, formData.leave_type]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error(t('file_type_not_allowed'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('file_too_large'));
      return;
    }
    setAttachmentFile(file);
  };

  const uploadAttachment = async () => {
    if (!attachmentFile) return null;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', attachmentFile);
      const { data } = await axios.post(`${API}/leaves/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.url;
    } catch (error) {
      toast.error(t('file_upload_failed'));
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.start_date || !formData.end_date) return;

    // Destination is mandatory for annual leaves (except no-paid-leave
    // employees who don't get flight tickets anyway). Mirrors the backend
    // guard in `routes/leaves.create_leave_request`.
    if (formData.leave_type === 'annual'
        && !hasNoPaidLeave(user)
        && !(formData.destination_city || '').trim()) {
      toast.error(language === 'ar'
        ? 'مدينة الوصول إلزامية للإجازة السنوية'
        : 'Destination city is required for annual leave');
      return;
    }

    // Block reversed date ranges before round-tripping to the backend.
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error(t('end_date_before_start') || 'تاريخ النهاية يجب أن يكون مساوياً أو بعد تاريخ البداية');
      return;
    }

    // Backdating: annual & unpaid must start today or later. Sick leave is
    // exempt (medical certificate covers retroactive cases). Mirror of the
    // server-side guard in `routes/leaves.create_leave_request`.
    if (['annual', 'unpaid'].includes(formData.leave_type)) {
      // Compare YYYY-MM-DD strings in KSA local time — avoids the timezone
      // off-by-one bug that hits users with `new Date()` near midnight.
      const ksaTodayIso = new Date(Date.now() + 3 * 60 * 60 * 1000)
        .toISOString().slice(0, 10);
      if (formData.start_date < ksaTodayIso) {
        const label = formData.leave_type === 'annual'
          ? (language === 'ar' ? 'السنوية' : 'annual')
          : (language === 'ar' ? 'بدون راتب' : 'unpaid');
        toast.error(language === 'ar'
          ? `لا يمكن تقديم طلب إجازة ${label} بتاريخ بدء قبل اليوم (${ksaTodayIso}).`
          : `Cannot submit a ${label} leave with a start date before today (${ksaTodayIso}).`);
        return;
      }
    }

    if (formData.leave_type === 'sick' && !attachmentFile && !formData.attachment_url) {
      toast.error(t('sick_leave_attachment_required'));
      return;
    }

    setSubmitting(true);
    try {
      let attachment_url = formData.attachment_url;
      if (attachmentFile) {
        attachment_url = await uploadAttachment();
        if (formData.leave_type === 'sick' && !attachment_url) {
          setSubmitting(false);
          return;
        }
      }
      await axios.post(`${API}/leaves`, { ...formData, attachment_url }, { withCredentials: true });
      toast.success(t('leave_submitted'));
      setShowRequestDialog(false);
      resetForm();
      fetchLeaves();
      fetchBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ leave_type: 'annual', start_date: '', end_date: '', reason: '', attachment_url: null, destination_city: '' });
    setAttachmentFile(null);
  };

  const calculateDays = () => {
    if (!formData.start_date || !formData.end_date) return 0;
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    if (end < start) return 0;
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  const getLeaveTypeLabel = (type) => {
    const types = { annual: t('annual_leave'), sick: t('sick_leave'), unpaid: t('unpaid_leave') };
    return types[type] || type;
  };

  const getStatusLabel = (status) => {
    const map = {
      pending: language === 'ar' ? 'بانتظار' : 'Pending',
      pending_manager: language === 'ar' ? 'بانتظار مدير القسم' : 'Awaiting Dept. Manager',
      pending_gm: language === 'ar' ? 'بانتظار المدير العام' : 'Awaiting General Manager',
      pending_admin: language === 'ar' ? 'بانتظار مدير النظام' : 'Awaiting System Admin',
      approved: language === 'ar' ? 'معتمد' : 'Approved',
      rejected: language === 'ar' ? 'مرفوض' : 'Rejected',
    };
    return map[status] || status;
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      pending_manager: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      pending_gm: 'bg-orange-100 text-orange-700 border-orange-200',
      pending_admin: 'bg-blue-100 text-blue-700 border-blue-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return variants[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const requestedDays = calculateDays();

  // Renders the appropriate clearance CTA / status for a given approved
  // annual leave row — reused by both the desktop table AND the mobile
  // card list so we don't drift between the two layouts.
  const renderClearanceCell = (leave) => {
    const isAnnualApproved = leave.leave_type === 'annual' && leave.status === 'approved';
    if (!isAnnualApproved) return <span className="text-[#655dc6]/30">-</span>;
    if (hasNoPaidLeave(user)) return <span className="text-[#655dc6]/30">-</span>;
    const days = (function () {
      try {
        const s = new Date(leave.start_date);
        const e = new Date(leave.end_date);
        return Math.max(0, Math.round((e - s) / 86400000) + 1);
      } catch { return 0; }
    })();
    if (days < 30) {
      return (
        <span
          className="text-[10px] text-[#655dc6]/60 italic leading-tight"
          title={language === 'ar'
            ? `الإجازة ${days} يوم — تُخصم من الرصيد وتُدفع مع الراتب، بدون مخالصة منفصلة (المخالصة للإجازة 30 يوماً فقط).`
            : `Leave is ${days} days — paid as salary, deducted from balance. Clearance only applies to a full 30-day annual leave.`}
          data-testid={`no-clearance-short-${leave.id}`}
        >
          {language === 'ar' ? 'مدفوعة مع الراتب' : 'Paid with salary'}
        </span>
      );
    }
    const c = clearanceByLeaveId[leave.id];
    if (!c) {
      return (
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1 border-emerald-400 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
          disabled={requestingClearanceLeaveId === leave.id}
          onClick={() => requestClearance(leave.id)}
          data-testid={`request-clearance-btn-${leave.id}`}
        >
          <Receipt size={14} weight="duotone" />
          {language === 'ar' ? 'طلب مخالصة' : 'Request Clearance'}
        </Button>
      );
    }
    if (c.status === 'pending_admin') {
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 text-xs">{language === 'ar' ? 'قيد المراجعة' : 'Under Review'}</Badge>;
    }
    if (c.status === 'pending_employee_signature') {
      const daysLeft = c.days_remaining_for_signature;
      const hoursLeft = c.hours_remaining_for_signature;
      let badge = null;
      if (daysLeft != null) {
        const isUrgent = daysLeft <= 2;
        const label = daysLeft > 0
          ? (language === 'ar' ? `متبقي ${daysLeft} يوم` : `${daysLeft}d left`)
          : (language === 'ar' ? `متبقي ${hoursLeft || 0} ساعة` : `${hoursLeft || 0}h left`);
        badge = (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${isUrgent ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-amber-100 text-amber-800 border border-amber-200'}`}
            data-testid={`sign-countdown-${c.id}`}
            title={language === 'ar'
              ? 'يجب التوقيع خلال 10 أيام من اعتماد الإدارة وإلا تُلغى المخالصة تلقائياً'
              : 'Sign within 10 days of approval — the clearance is auto-cancelled otherwise'}
          >
            ⏳ {label}
          </span>
        );
      }
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button
            size="sm"
            className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white animate-pulse"
            onClick={() => openClearanceDetails(c.id)}
            data-testid={`sign-clearance-btn-${c.id}`}
          >
            <Signature size={14} weight="duotone" />
            {language === 'ar' ? 'مراجعة وتوقيع' : 'Review & Sign'}
          </Button>
          {badge}
        </div>
      );
    }
    if (c.status === 'cancelled_unsigned') {
      return <Badge className="bg-red-100 text-red-800 border-red-200 text-xs" title={language === 'ar' ? 'تم الإلغاء تلقائياً لعدم التوقيع خلال 10 أيام' : 'Auto-cancelled — not signed within 10 days'}>{language === 'ar' ? 'مُلغى (لم يُوقَّع)' : 'Cancelled (unsigned)'}</Badge>;
    }
    if (c.status === 'signed') {
      return (
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1 border-[#655dc6] text-[#655dc6] hover:bg-[#E8E6F5] hover:text-[#544cb0]"
          onClick={() => downloadClearancePdf(c.id)}
          data-testid={`download-clearance-btn-${c.id}`}
        >
          <Download size={14} weight="duotone" />
          {language === 'ar' ? 'تنزيل PDF' : 'Download PDF'}
        </Button>
      );
    }
    if (c.status === 'rejected') {
      const reason = (c.admin_notes || '').trim();
      return (
        <div className="flex flex-col gap-1.5" data-testid={`clearance-rejected-block-${c.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">{language === 'ar' ? 'مرفوض' : 'Rejected'}</Badge>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1 border-emerald-400 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
              disabled={requestingClearanceLeaveId === leave.id}
              onClick={() => requestClearance(leave.id)}
              data-testid={`resubmit-clearance-btn-${leave.id}`}
              title={language === 'ar' ? 'إعادة تقديم المخالصة بعد الرفض' : 'Resubmit clearance after rejection'}
            >
              <Receipt size={14} weight="duotone" />
              {language === 'ar' ? 'إعادة تقديم' : 'Resubmit'}
            </Button>
          </div>
          {reason && (
            <p className="text-[11px] text-red-700 max-w-[220px] leading-relaxed"
               title={reason}
               data-testid={`clearance-rejection-reason-${c.id}`}>
              <strong>{language === 'ar' ? 'سبب الرفض: ' : 'Reason: '}</strong>{reason}
            </p>
          )}
        </div>
      );
    }
    return <span className="text-[#655dc6]/30">-</span>;
  };

  // Build the leave info notification based on selected type
  const renderLeaveInfo = () => {
    const type = formData.leave_type;
    const rule = LEAVE_RULES[type];
    if (!rule) return null;

    const remaining = leaveBalance?.remaining_days ?? 0;
    const entitlement = leaveBalance?.annual_entitlement ?? 21;
    const prorated = leaveBalance?.prorated_entitlement ?? entitlement;
    const sickUsed = leaveBalance?.sick_days_used ?? 0;

    if (type === 'annual') {
      // Contracted workers: leave is informational only — no balance,
      // no exceed-warning, just a short notice.
      if (hasNoPaidLeave(user)) {
        return (
          <div className="p-3 rounded-lg border bg-amber-50 border-amber-200" data-testid="leave-info-banner">
            <div className="flex items-start gap-2">
              <Info size={20} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">
                {language === 'ar'
                  ? 'إجازتك تُسجَّل للمعرفة والاعتماد فقط.'
                  : 'Your leave is recorded for awareness and approval only.'}
              </p>
            </div>
          </div>
        );
      }
      // When the leave start_date is set, prefer the BACKEND projection
      // — it accounts for future accrual the employee will earn between
      // today and start_date. Falls back to today's balance only when no
      // projection is available yet (e.g. before the start date is set).
      const projectionLoaded = projection && typeof projection.fits === 'boolean';
      const availableForCheck = projectionLoaded ? (projection.available_at_start ?? remaining) : remaining;
      const willExceed = projectionLoaded ? !projection.fits : (requestedDays > remaining);
      return (
        <div className={`p-3 rounded-lg border ${willExceed ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`} data-testid="leave-info-banner">
          <div className="flex items-start gap-2">
            {willExceed ? <WarningCircle size={20} weight="fill" className="text-red-500 mt-0.5 shrink-0" /> : <Info size={20} weight="fill" className="text-blue-500 mt-0.5 shrink-0" />}
            <div className="text-sm space-y-1">
              <p className={willExceed ? 'text-red-700 font-medium' : 'text-blue-700 font-medium'}>
                {t('annual_leave_info')}
              </p>
              <p className="text-blue-600/80">
                {projectionLoaded && (projection.future_accrual_days || 0) > 0 ? (
                  <>
                    {language === 'ar' ? 'الرصيد المتوقّع عند تاريخ بداية الإجازة' : 'Projected balance at start date'}:&nbsp;
                    <strong>{availableForCheck}</strong> {t('day')}
                    <span className="text-blue-500/60"> ({language === 'ar' ? 'اليوم' : 'today'}: {remaining} + {language === 'ar' ? 'مستحق' : 'will earn'}: {projection.future_accrual_days})</span>
                  </>
                ) : (
                  <>
                    {t('remaining_balance')}: <strong>{availableForCheck}</strong> {t('days_of')} {prorated} {t('day')}
                    {prorated < entitlement && <span className="text-blue-500/60"> ({t('prorated_from')} {entitlement})</span>}
                  </>
                )}
              </p>
              {requestedDays > 0 && (
                <p className={willExceed ? 'text-red-600 font-medium' : 'text-blue-600/80'}>
                  {t('requested_days')}: <strong>{requestedDays}</strong> {t('day')}
                  {willExceed && ` - ${t('exceeds_balance')}`}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (type === 'sick') {
      // Percentage doctors & contracted workers: no balance impact
      // messaging — leaves are informational only, same as annual.
      if (hasNoPaidLeave(user)) {
        return (
          <div className="p-3 rounded-lg border bg-amber-50 border-amber-200" data-testid="leave-info-banner">
            <div className="flex items-start gap-2">
              <Info size={20} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">
                {language === 'ar'
                  ? 'إجازتك تُسجَّل للمعرفة والاعتماد فقط.'
                  : 'Your leave is recorded for awareness and approval only.'}
              </p>
            </div>
          </div>
        );
      }
      return (
        <div className="p-3 rounded-lg border bg-amber-50 border-amber-200" data-testid="leave-info-banner">
          <div className="flex items-start gap-2">
            <Info size={20} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
            <div className="text-sm space-y-1">
              <p className="text-amber-700 font-medium">{t('sick_leave_info')}</p>
              <p className="text-amber-700 font-medium flex items-center gap-1 mt-1">
                <CheckCircle size={16} weight="fill" className="text-green-500" />
                {t('no_annual_deduction')}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (type === 'unpaid') {
      // Percentage doctors & contracted workers: same simple message.
      if (hasNoPaidLeave(user)) {
        return (
          <div className="p-3 rounded-lg border bg-amber-50 border-amber-200" data-testid="leave-info-banner">
            <div className="flex items-start gap-2">
              <Info size={20} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">
                {language === 'ar'
                  ? 'إجازتك تُسجَّل للمعرفة والاعتماد فقط.'
                  : 'Your leave is recorded for awareness and approval only.'}
              </p>
            </div>
          </div>
        );
      }
      return (
        <div className="p-3 rounded-lg border bg-red-50 border-red-200" data-testid="leave-info-banner">
          <div className="flex items-start gap-2">
            <XCircle size={20} weight="fill" className="text-red-500 mt-0.5 shrink-0" />
            <div className="text-sm space-y-1">
              <p className="text-red-700 font-medium">{t('unpaid_leave_info')}</p>
              <p className="text-red-600/80 flex items-center gap-1">
                <CheckCircle size={16} weight="fill" className="text-green-500" />
                {t('no_annual_deduction')}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderApprovalDisclaimer = () => {
    const type = formData.leave_type;
    if (!type || type === 'sick') return null;
    return (
      <div className="p-3 rounded-lg border bg-[#E8E6F5] border-[#C4BFF0]" data-testid="approval-disclaimer">
        <p className="text-sm text-[#655dc6]/80 text-center leading-relaxed">
          {language === 'ar'
            ? 'نود التنويه بأن الموافقة على طلب الإجازة تخضع لتقدير الإدارة وفقاً لمتطلبات سير العمل، ونقدّر تفهّمكم وتعاونكم'
            : 'Please note that leave approval is subject to management discretion based on operational requirements. We appreciate your understanding.'}
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction} data-testid="employee-leaves-page">
      {/* Header — sticky on mobile so the brand & back button remain accessible
          while the user scrolls long content. On larger screens it stays normal. */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-[#655dc6]">{t('my_leaves')}</h1>
            {leaveBalance && !hasNoPaidLeave(user) && (
              <p className="text-sm text-[#655dc6]/60 mt-1">
                {t('remaining_balance')}: <strong className={leaveBalance.remaining_days < 0 ? 'text-red-600' : 'text-[#655dc6]'}>{leaveBalance.remaining_days}</strong> {t('days_of')} {leaveBalance.prorated_entitlement} {t('day')}
                {leaveBalance.prorated_entitlement < leaveBalance.annual_entitlement && (
                  <span className="text-[#655dc6]/40"> ({t('prorated_from')} {leaveBalance.annual_entitlement})</span>
                )}
              </p>
            )}
            {hasNoPaidLeave(user) && (
              <p className="text-sm text-amber-700/90 mt-1 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 inline-block" data-testid="contracted-worker-leave-note">
                {language === 'ar'
                  ? '🔔 إجازاتك تُقدَّم للمعرفة والاعتماد فقط.'
                  : '🔔 Your leaves are filed for awareness and approval only.'}
              </p>
            )}
          </div>
          <Button onClick={() => setShowRequestDialog(true)} className="bg-[#655dc6] hover:bg-[#544cb0] text-white" data-testid="request-leave-btn">
            <CalendarPlus size={20} className="me-2" />
            {t('request_leave')}
          </Button>
        </div>

        {/* Cash-only encashment (no travel) — once per calendar year, routes
            directly to the Finance Manager, deducts days on signature.
            Hidden for contracted workers (they have no leave balance). */}
        {!hasNoPaidLeave(user) && (
          <CashOnlyEncashmentSection onOpenClearance={openClearanceDetails} />
        )}

        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
              <CalendarCheck size={22} weight="duotone" className="text-[#655dc6]" />
              {t('leave_history')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
              </div>
            ) : (
              <>
              {/* Mobile Card List (<640px) */}
              <div className="sm:hidden space-y-3" data-testid="leaves-mobile-list">
                {leaves.length === 0 ? (
                  <p className="text-center py-8 text-[#655dc6]/70 text-sm">{t('no_data')}</p>
                ) : (
                  leaves.map((leave) => {
                    const s = new Date(leave.start_date);
                    const e = new Date(leave.end_date);
                    const days = Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
                    const showProgress = leave.status !== 'approved' && leave.status !== 'rejected';
                    return (
                      <div
                        key={leave.id}
                        data-testid={`leave-card-${leave.id}`}
                        className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                      >
                        {/* Header: type + status */}
                        <div className="flex items-start justify-between gap-2 pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                          <div className="min-w-0">
                            <h4 className="text-base font-bold text-[#262263] dark:text-slate-100 line-clamp-1">
                              {getLeaveTypeLabel(leave.leave_type)}
                            </h4>
                            <p className="text-[11px] text-[#655dc6]/60 dark:text-slate-400 mt-0.5">
                              {language === 'ar' ? 'المدة' : 'Duration'}: <strong>{days}</strong> {t('day')}
                            </p>
                          </div>
                          <Badge className={`${getStatusBadge(leave.status)} shrink-0 text-[11px]`}>
                            {getStatusLabel(leave.status)}
                          </Badge>
                        </div>

                        {/* Metadata grid */}
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                          <div>
                            <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('start_date')}</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono">{leave.start_date}</span>
                          </div>
                          <div>
                            <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('end_date')}</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono">{leave.end_date}</span>
                          </div>
                          {leave.reason && (
                            <div className="col-span-2">
                              <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('reason')}</span>
                              <span className="text-slate-700 dark:text-slate-300 line-clamp-2">{leave.reason}</span>
                            </div>
                          )}
                          {leave.attachment_url && (
                            <div className="col-span-2">
                              <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('attachment')}</span>
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${leave.attachment_url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[#655dc6] hover:underline"
                                data-testid={`attachment-link-mobile-${leave.id}`}
                              >
                                {leave.attachment_url.endsWith('.pdf') ? <FileText size={16} /> : <Image size={16} />}
                                {t('view')}
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Approval progress for pending */}
                        {showProgress && (
                          <div className="pt-2 mb-2 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                            <ApprovalProgress steps={leaveProgressSteps(leave)} />
                          </div>
                        )}

                        {/* Clearance CTA (annual approved only) */}
                        {leave.leave_type === 'annual' && leave.status === 'approved' && !hasNoPaidLeave(user) && (
                          <div className="pt-3 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                            <div className="text-[10px] text-[#655dc6]/60 dark:text-slate-500 font-medium mb-1.5 uppercase tracking-wide">
                              {language === 'ar' ? 'المخالصة' : 'Clearance'}
                            </div>
                            {renderClearanceCell(leave)}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Desktop Table (>=640px) */}
              <div className="hidden sm:block overflow-x-auto scrollbar-thin" data-mobile-hint="scroll-x">
                <Table data-testid="leaves-table">
                  <TableHeader>
                    <TableRow className="border-[#C4BFF0]">
                      <TableHead className="text-start">{t('leave_type')}</TableHead>
                      <TableHead className="text-start">{t('start_date')}</TableHead>
                      <TableHead className="text-start">{t('end_date')}</TableHead>
                      <TableHead className="text-start">{t('days')}</TableHead>
                      <TableHead className="text-start">{t('reason')}</TableHead>
                      <TableHead className="text-start">{t('attachment')}</TableHead>
                      <TableHead className="text-start">{t('status')}</TableHead>
                      <TableHead className="text-start">{language === 'ar' ? 'المخالصة' : 'Clearance'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaves.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-[#655dc6]/70">{t('no_data')}</TableCell>
                      </TableRow>
                    ) : (
                      leaves.map((leave) => {
                        const s = new Date(leave.start_date);
                        const e = new Date(leave.end_date);
                        const days = Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
                        const showProgress = leave.status !== 'approved' && leave.status !== 'rejected';
                        return (
                          <React.Fragment key={leave.id}>
                          <TableRow className="border-[#C4BFF0]">
                            <TableCell className="font-medium text-[#655dc6]">{getLeaveTypeLabel(leave.leave_type)}</TableCell>
                            <TableCell className="text-[#655dc6]/70">{leave.start_date}</TableCell>
                            <TableCell className="text-[#655dc6]/70">{leave.end_date}</TableCell>
                            <TableCell className="text-[#655dc6]/70">{days}</TableCell>
                            <TableCell className="text-[#655dc6]/70 max-w-[200px] truncate">{leave.reason || '-'}</TableCell>
                            <TableCell>
                              {leave.attachment_url ? (
                                <a href={`${process.env.REACT_APP_BACKEND_URL}${leave.attachment_url}`} target="_blank" rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[#655dc6] hover:underline text-sm" data-testid={`attachment-link-${leave.id}`}>
                                  {leave.attachment_url.endsWith('.pdf') ? <FileText size={16} /> : <Image size={16} />}
                                  {t('view')}
                                </a>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusBadge(leave.status)}>{getStatusLabel(leave.status)}</Badge>
                            </TableCell>
                            <TableCell>
                              {renderClearanceCell(leave)}
                            </TableCell>
                          </TableRow>
                          {showProgress && (
                            <TableRow className="border-[#C4BFF0] bg-[#F8F7FC]">
                              <TableCell colSpan={8} className="py-3 px-4">
                                <ApprovalProgress steps={leaveProgressSteps(leave)} />
                              </TableCell>
                            </TableRow>
                          )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Request Leave Dialog */}
        <Dialog open={showRequestDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowRequestDialog(open); }}>
          <DialogContent
            className="sm:max-w-[500px] max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden"
            dir={direction}
            data-testid="request-leave-dialog"
          >
            <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-[#C4BFF0]/40">
              <DialogTitle className="flex items-center gap-2">
                <CalendarPlus size={24} weight="duotone" className="text-[#655dc6]" />
                {t('request_leave')}
              </DialogTitle>
              <DialogDescription>{t('fill_leave_details')}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4 px-6 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-2">
                <Label>{t('leave_type')}</Label>
                <Select value={formData.leave_type} onValueChange={(value) => { setFormData({ ...formData, leave_type: value, attachment_url: null }); setAttachmentFile(null); }}>
                  <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">{t('annual_leave')}</SelectItem>
                    <SelectItem value="sick">{t('sick_leave')}</SelectItem>
                    <SelectItem value="unpaid">{t('unpaid_leave')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Leave Info Banner */}
              {renderLeaveInfo()}
              {renderApprovalDisclaimer()}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('start_date')}</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    min={['annual', 'unpaid'].includes(formData.leave_type)
                      ? new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 10)
                      : undefined}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full bg-[#E8E6F5] border-[#C4BFF0]"
                    data-testid="leave-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('end_date')}</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    min={formData.start_date || (['annual', 'unpaid'].includes(formData.leave_type)
                      ? new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 10)
                      : undefined)}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full bg-[#E8E6F5] border-[#C4BFF0]"
                    data-testid="leave-end-date"
                  />
                </div>
              </div>

              {requestedDays > 0 && (
                <p className="text-sm text-[#655dc6]/70 text-center">
                  {t('total_requested')}: <strong className="text-[#655dc6]">{requestedDays}</strong> {t('day')}
                </p>
              )}

              {/* Live projection calculator — annual leave only.
                  Hidden for contracted workers (no balance, no validation). */}
              {projection && formData.leave_type === 'annual' && formData.start_date && !hasNoPaidLeave(user) && (
                <div
                  className={`rounded-lg border p-3 text-xs space-y-1 ${
                    projection.fits
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                  data-testid="leave-balance-projection"
                >
                  <div className="flex items-center justify-between font-semibold">
                    <span className={projection.fits ? 'text-emerald-700' : 'text-red-700'}>
                      {language === 'ar' ? 'حاسبة الرصيد' : 'Balance Calculator'}
                    </span>
                    <span className={projection.fits ? 'text-emerald-600' : 'text-red-600'}>
                      {projection.fits
                        ? (language === 'ar' ? '✓ الرصيد كافٍ' : '✓ Sufficient')
                        : (language === 'ar' ? '✗ الرصيد غير كافٍ' : '✗ Insufficient')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[#655dc6]/80">
                    <div className="flex justify-between border-b border-dashed border-current/20 pb-0.5">
                      <span>
                        {formData.leave_type === 'annual' && projection.as_of_date !== new Date().toISOString().slice(0, 10)
                          ? (language === 'ar' ? `الرصيد المتوقع بتاريخ ${projection.as_of_date}` : `Projected balance at ${projection.as_of_date}`)
                          : (language === 'ar' ? 'الرصيد المستحق' : 'Entitlement')}
                        :
                      </span>
                      <strong>{projection.projected_entitlement}</strong>
                    </div>
                    <div className="flex justify-between border-b border-dashed border-current/20 pb-0.5">
                      <span>{language === 'ar' ? 'المستخدم/المعلق هذه السنة' : 'Used/pending this year'}:</span>
                      <strong>− {projection.already_consumed_days}</strong>
                    </div>
                    <div className="flex justify-between border-b border-dashed border-current/20 pb-0.5">
                      <span>{language === 'ar' ? 'المتاح للحجز الآن' : 'Available to book'}:</span>
                      <strong>{projection.available_at_start}</strong>
                    </div>
                    <div className="flex justify-between border-b border-dashed border-current/20 pb-0.5">
                      <span>{language === 'ar' ? 'هذا الطلب يستهلك' : 'This request consumes'}:</span>
                      <strong>− {projection.requested_days}</strong>
                    </div>
                    <div className="col-span-2 flex justify-between font-semibold pt-1">
                      <span>{language === 'ar' ? 'الرصيد بعد الطلب' : 'Balance after request'}:</span>
                      <strong className={projection.fits ? 'text-emerald-700' : 'text-red-700'}>
                        {projection.balance_after_request}
                      </strong>
                    </div>
                  </div>
                  {formData.leave_type === 'annual' && projection.as_of_date !== new Date().toISOString().slice(0, 10) && (
                    <p className="text-[10px] text-[#655dc6]/50 pt-1 border-t border-current/10">
                      {language === 'ar'
                        ? `يُحسب رصيد الإجازة السنوية بناءً على تاريخ بداية الإجازة (${projection.annual_days_policy} يوم/سنة).`
                        : `Annual leave balance is projected to the start date (${projection.annual_days_policy} days/year).`}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('reason')}</Label>
                <Textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder={t('optional')} className="bg-[#E8E6F5] border-[#C4BFF0] resize-none" rows={2} data-testid="leave-reason" />
              </div>

              {/* Destination City — annual leaves only. Surfaces a clear
                  disclaimer about the 3-month advance heuristic for direct
                  flights and the no-guarantee policy.
                  Agency contracted workers are not entitled to flight
                  tickets, so this whole block is hidden for them. */}
              {formData.leave_type === 'annual' && !hasNoPaidLeave(user) && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <span>✈️</span>
                    {language === 'ar' ? 'مدينة الوصول' : 'Destination City'}
                    <span className="text-red-500 ms-1" aria-hidden="true">*</span>
                  </Label>
                  <Input
                    type="text"
                    value={formData.destination_city}
                    onChange={(e) => setFormData({ ...formData, destination_city: e.target.value })}
                    placeholder={language === 'ar' ? 'مثال: القاهرة، إسطنبول، دبي…' : 'e.g. Cairo, Istanbul, Dubai…'}
                    className="bg-[#E8E6F5] border-[#C4BFF0]"
                    required
                    data-testid="leave-destination-city"
                  />
                  <div
                    className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11px] leading-relaxed text-amber-900"
                    data-testid="destination-flight-note"
                  >
                    <p className="font-semibold mb-1">
                      {language === 'ar' ? '🛫 ملاحظة بخصوص حجز التذاكر:' : '🛫 Flight booking notice:'}
                    </p>
                    <p>
                      {language === 'ar'
                        ? 'في حال تقديم طلب الإجازة قبل موعد استحقاقها بثلاثة أشهر أو أكثر، تزداد فرصة الحصول على تذكرة طيران رحلة مباشرة.'
                        : 'Requesting leave at least 3 months before its start date increases the chance of receiving a direct flight ticket.'}
                    </p>
                    <p className="mt-1.5 font-medium">
                      {language === 'ar'
                        ? '⚠️ على كل الأحوال، الشركة لا تضمن حجز تذكرة طيران مباشرة.'
                        : '⚠️ The company does not guarantee a direct flight booking under any circumstances.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Sick Leave Attachment */}
              {formData.leave_type === 'sick' && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Paperclip size={16} />
                    {t('medical_certificate')} <span className="text-red-500">*</span>
                  </Label>
                  <div className="border-2 border-dashed border-[#C4BFF0] rounded-lg p-4 text-center hover:border-[#655dc6] transition-colors">
                    <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleFileChange} className="hidden" id="sick-leave-file" data-testid="sick-leave-file-input" />
                    <label htmlFor="sick-leave-file" className="cursor-pointer">
                      {attachmentFile ? (
                        <div className="flex items-center justify-center gap-2 text-[#655dc6]">
                          {attachmentFile.type === 'application/pdf' ? <FileText size={24} /> : <Image size={24} />}
                          <span className="text-sm font-medium">{attachmentFile.name}</span>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <Paperclip size={28} className="mx-auto text-[#655dc6]/40" />
                          <p className="text-sm text-[#655dc6]/60">{t('click_to_upload')}</p>
                          <p className="text-xs text-[#655dc6]/40">{t('allowed_formats')}</p>
                        </div>
                      )}
                    </label>
                  </div>
                  <p className="text-[11px] text-[#655dc6]/50 mt-1" data-testid="sehhaty-note">{t('sehhaty_note')}</p>
                </div>
              )}
            </div>
            <DialogFooter className="px-6 py-4 shrink-0 border-t border-[#C4BFF0]/40 bg-white/95 backdrop-blur">
              <Button variant="outline" onClick={() => { resetForm(); setShowRequestDialog(false); }}>{t('cancel')}</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || uploadingFile || !formData.start_date || !formData.end_date}
                className="bg-[#655dc6] hover:bg-[#544cb0]"
                data-testid="submit-leave-btn"
              >
                {(submitting || uploadingFile) ? <SpinnerGap size={20} className="animate-spin" /> : t('submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ========= Clearance Signature Dialog ========= */}
        <Dialog open={!!activeClearance} onOpenChange={(o) => !o && setActiveClearance(null)}>
          <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" dir={direction} data-testid="clearance-sign-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-700">
                <Receipt size={22} weight="duotone" />
                {language === 'ar' ? 'مراجعة وتوقيع المخالصة' : 'Review & Sign Clearance'}
              </DialogTitle>
              <DialogDescription>
                {language === 'ar' ? 'يرجى مراجعة التفاصيل بعناية والتوقيع للموافقة على المبلغ النهائي.' : 'Please review the details carefully and sign to confirm the final amount.'}
              </DialogDescription>
            </DialogHeader>

            {activeClearance && activeClearance.preview && (
              <div className="space-y-3 text-sm">
                {/* Approval progress */}
                <div className="px-3 py-3 bg-white border border-[#C4BFF0] rounded">
                  <ApprovalProgress steps={clearanceProgressSteps(activeClearance)} />
                </div>

                {/* Leave block */}
                <div className="grid grid-cols-3 gap-2 p-3 bg-[#E8E6F5] rounded">
                  <div><div className="text-[10px] text-[#655dc6]/70">{language === 'ar' ? 'بداية الإجازة' : 'Start'}</div><div className="font-medium">{activeClearance.preview.leave_start}</div></div>
                  <div><div className="text-[10px] text-[#655dc6]/70">{language === 'ar' ? 'نهاية الإجازة' : 'End'}</div><div className="font-medium">{activeClearance.preview.leave_end}</div></div>
                  <div><div className="text-[10px] text-[#655dc6]/70">{language === 'ar' ? 'عدد الأيام' : 'Days'}</div><div className="font-medium">{activeClearance.preview.leave_days}</div></div>
                </div>

                {/* Calculation */}
                <div className="p-3 border border-[#C4BFF0] rounded space-y-1">
                  {/* Approved days (always visible — no monetary info leak) */}
                  <div className="flex justify-between"><span>{language === 'ar' ? 'الرصيد المتبقي' : 'Remaining Balance'}</span><span className="font-medium">{activeClearance.preview.remaining_days} {language === 'ar' ? 'يوم' : 'days'}</span></div>
                  <div className="flex justify-between text-emerald-700"><span className="font-semibold">{language === 'ar' ? 'أيام الصرف المعتمدة' : 'Approved Payout Days'}</span><span className="font-bold">{activeClearance.final_payout_days}</span></div>
                </div>

                {/* Gross/Deductions/Net — ONLY visible to the employee AFTER
                    the Finance Manager has approved (status advances to
                    `pending_employee_signature`) or once the doc is fully
                    signed. Before that, the employee sees no monetary
                    information — even if the System Admin has already
                    locked an internal amount. This matches company policy
                    (v1.4): numbers become public to the employee only
                    when they're about to sign on them. */}
                {(activeClearance.status !== 'pending_employee_signature' && activeClearance.status !== 'signed') ? (
                  <div className="p-4 border-2 border-dashed border-[#C4BFF0] rounded-lg bg-[#F2F0FA] text-center">
                    <p className="text-sm font-semibold text-[#655dc6]">
                      {language === 'ar'
                        ? 'المبالغ قيد المراجعة من قِبَل المدير المالي'
                        : 'Amounts are under review by the Finance Manager'}
                    </p>
                    <p className="text-[11px] text-[#655dc6]/70 mt-1 leading-relaxed">
                      {language === 'ar'
                        ? 'سيظهر لك المبلغ النهائي هنا بعد الاعتماد من المدير المالي، وستُطالَب حينها بالتوقيع على رقم ثابت لا يتغيّر.'
                        : 'The final amount will appear here once the Finance Manager approves it. You will then sign on a fixed, unchanging figure.'}
                    </p>
                  </div>
                ) : (
                  <div className="p-3 border-2 border-[#655dc6] rounded bg-[#F2F0FA] space-y-1">
                    <div className="flex justify-between"><span>{language === 'ar' ? 'إجمالي بدل الإجازة' : 'Gross Allowance'}</span><span className="font-semibold">{(activeClearance.final_gross || 0).toLocaleString()} <span className="text-xs">SAR</span></span></div>
                    {(activeClearance.deductions || []).map((d, i) => (
                      <div key={i} className="flex justify-between text-red-600 text-xs"><span>{d.label}</span><span>-{d.amount.toLocaleString()}</span></div>
                    ))}
                    {(activeClearance.deductions || []).length > 0 && (
                      <div className="flex justify-between text-red-700 border-t border-red-200 pt-1"><span className="font-medium">{language === 'ar' ? 'إجمالي الخصومات' : 'Total Deductions'}</span><span className="font-semibold">-{(activeClearance.total_deductions || 0).toLocaleString()} <span className="text-xs">SAR</span></span></div>
                    )}
                    <div className="flex justify-between text-lg pt-2 border-t-2 border-[#655dc6]"><span className="font-bold text-[#655dc6]">{language === 'ar' ? 'صافي المستحقات' : 'Net Payable'}</span><span className="font-extrabold text-[#655dc6]">{(activeClearance.final_net || 0).toLocaleString()} <span className="text-xs">SAR</span></span></div>
                  </div>
                )}

                {activeClearance.admin_notes && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[12px] text-amber-800">
                    <strong>{language === 'ar' ? 'ملاحظات الإدارة:' : 'Admin notes:'}</strong> {activeClearance.admin_notes}
                  </div>
                )}

                {/* Acknowledgement */}
                <div className="p-3 bg-amber-50 border border-amber-300 rounded text-[12px] text-amber-900 leading-relaxed">
                  {language === 'ar'
                    ? 'بتوقيعك أدناه، تُقرّ بأن المبلغ الصافي أعلاه هو كامل مستحقاتك عن أيام الإجازة المعتمدة، ولا تحقّ المطالبة بأي مبالغ إضافية تخص هذه الفترة.'
                    : 'By signing below, you acknowledge that the net amount above is your full dues for the approved leave days, and you waive any further claims for this period.'}
                </div>

                <div className="space-y-2">
                  <Label>{language === 'ar' ? 'اكتب اسمك الكامل للتوقيع الإلكتروني' : 'Type your full name to sign electronically'}</Label>
                  <Input
                    placeholder={activeClearance.preview.employee_name}
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    className="bg-emerald-50 border-emerald-300 focus:ring-emerald-500"
                    data-testid="signature-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>
                      {language === 'ar' ? 'ارسم توقيعك بإصبعك أو الفأرة' : 'Draw your signature (finger or mouse)'}
                      <span className="text-red-500"> *</span>
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => { signaturePadRef.current?.clear(); setSignaturePadEmpty(true); }}
                      className="text-[#655dc6] hover:bg-[#E8E6F5] gap-1 h-7 px-2"
                      data-testid="clear-signature-btn"
                    >
                      <Eraser size={16} />
                      {language === 'ar' ? 'مسح' : 'Clear'}
                    </Button>
                  </div>
                  <SignaturePad
                    ref={signaturePadRef}
                    height={160}
                    onChange={(empty) => setSignaturePadEmpty(empty)}
                    data-testid="employee-signature-pad"
                  />
                  <p className="text-[11px] text-[#655dc6]/60">
                    {language === 'ar'
                      ? 'سيظهر توقيعك اليدوي في نسخة المخالصة النهائية المطبوعة.'
                      : 'Your handwritten signature will appear on the final printed clearance.'}
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setActiveClearance(null)} disabled={signing}>
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </Button>
              <Button
                onClick={submitSignature}
                disabled={signing || !signatureName.trim() || signaturePadEmpty}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="confirm-sign-btn"
              >
                <Signature size={18} weight="duotone" />
                {signing ? '...' : (language === 'ar' ? 'توقيع وموافقة' : 'Sign & Agree')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
